import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Button,
  Tooltip
} from '@mui/material';
import {
  People as PeopleIcon,
  PersonAdd as FollowIcon,
  Visibility as ViewIcon,
  Favorite as LikeIcon,
  Chat as ChatIcon,
  CheckCircle as MatchIcon,
  PhotoLibrary as PhotoIcon,
  Description as DocIcon,
  Report as ReportIcon,
  Payments as PaymentIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import { verificationAPI } from '../services/api';
import toast from 'react-hot-toast';
import moment from 'moment';

const StatisticsDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await verificationAPI.getStats();
      setStats(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color, subtitle, trend }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {value || 0}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="textSecondary">
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Box display="flex" alignItems="center" mt={1}>
                {trend > 0 ? (
                  <TrendingIcon sx={{ color: 'success.main', fontSize: 16 }} />
                ) : (
                  <TrendingDownIcon sx={{ color: 'error.main', fontSize: 16 }} />
                )}
                <Typography variant="caption" color={trend > 0 ? 'success.main' : 'error.main'}>
                  {Math.abs(trend)}% {trend > 0 ? 'increase' : 'decrease'}
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar sx={{ bgcolor: color, width: 48, height: 48 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const StatusChip = ({ status }) => {
    const statusMap = {
      pending: { color: 'warning', label: 'Pending' },
      approved: { color: 'success', label: 'Approved' },
      accepted: { color: 'success', label: 'Accepted' },
      rejected: { color: 'error', label: 'Rejected' },
      reviewed: { color: 'info', label: 'Reviewed' },
      dismissed: { color: 'default', label: 'Dismissed' }
    };
    const info = statusMap[status] || { color: 'default', label: status };
    return <Chip size="small" color={info.color} label={info.label} />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (!stats) {
    return (
      <Paper sx={{ p: 5, textAlign: 'center' }}>
        <Typography variant="h6" color="textSecondary">
          No statistics available
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Dashboard Statistics
        </Typography>
        <Button variant="contained" startIcon={<RefreshIcon />} onClick={fetchStats}>
          Refresh
        </Button>
      </Box>

      {/* Today's Stats */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.light', color: 'white' }}>
        <Typography variant="h6" gutterBottom>
          Today's Activity
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption">Profile Views</Typography>
            <Typography variant="h5">{stats.today.views || 0}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption">Likes</Typography>
            <Typography variant="h5">{stats.today.likes || 0}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption">Requests</Typography>
            <Typography variant="h5">{stats.today.requests || 0}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption">Swipes</Typography>
            <Typography variant="h5">{stats.today.swipes || 0}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={stats.users.total}
            icon={<PeopleIcon />}
            color="#2196f3"
            subtitle={`${stats.users.verified} verified, ${stats.users.blocked} blocked`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Follows"
            value={stats.follows.total}
            icon={<FollowIcon />}
            color="#4caf50"
            subtitle={`${stats.follows.pending} pending, ${stats.follows.accepted} accepted`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Profile Views"
            value={stats.profileViews.total}
            icon={<ViewIcon />}
            color="#ff9800"
            subtitle={`${stats.profileViews.uniqueViewers} unique viewers`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Likes"
            value={stats.likes.total}
            icon={<LikeIcon />}
            color="#e91e63"
            subtitle={`${stats.likes.uniqueUsers} unique users`}
          />
        </Grid>
      </Grid>

      {/* Second Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Matches"
            value={stats.matches.total}
            icon={<MatchIcon />}
            color="#9c27b0"
            subtitle={`${stats.matches.accepted} accepted, ${stats.matches.pending} pending`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Chats"
            value={stats.chats.total}
            icon={<ChatIcon />}
            color="#00bcd4"
            subtitle={`${stats.chats.uniqueUsers} unique users`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Subscriptions"
            value={stats.subscriptions.active}
            icon={<PaymentIcon />}
            color="#f44336"
            subtitle={`Rs${stats.subscriptions.totalRevenue.toFixed(2)} revenue`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Reports"
            value={stats.reports.total}
            icon={<ReportIcon />}
            color="#795548"
            subtitle={`${stats.reports.pending} pending`}
          />
        </Grid>
      </Grid>

      {/* Verification Stats */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Verification Statistics
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              Documents
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Chip label={`Total: ${stats.documents.total}`} />
              <Chip label={`Pending: ${stats.documents.pending}`} color="warning" />
              <Chip label={`Approved: ${stats.documents.approved}`} color="success" />
              <Chip label={`Rejected: ${stats.documents.rejected}`} color="error" />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              Photos
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Chip label={`Profile: ${stats.photos.profile.total}`} />
              <Chip label={`Gallery: ${stats.photos.gallery.total}`} />
              <Chip label={`Pending: ${stats.photos.profile.pending + stats.photos.gallery.pending}`} color="warning" />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Recent Activities */}
      {stats.recentActivities && stats.recentActivities.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Recent Activities (Last 7 Days)
          </Typography>
          <Grid container spacing={2}>
            {stats.recentActivities.map((activity, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">
                      {moment(activity.date).format('MMM DD, YYYY')}
                    </Typography>
                    <Typography variant="h6">
                      {activity.activity_count}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {activity.interaction_type.replace('_', ' ')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Top Users */}
      {stats.topUsers && stats.topUsers.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Top Active Users
          </Typography>
          <List>
            {stats.topUsers.map((user, index) => (
              <ListItem key={user.id} divider={index < stats.topUsers.length - 1}>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    {index + 1}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`${user.first_name || ''} ${user.last_name || ''}`}
                  secondary={`Phone: ${user.phone || 'N/A'} • ${user.interaction_count} interactions`}
                />
                <Chip 
                  size="small" 
                  label={`${user.interaction_count} activities`} 
                  color="primary" 
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default StatisticsDashboard;